import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { ReclamationType } from 'graphql-ts-types';
import type { SpaceProps } from 'styled-system';
import { space } from 'styled-system';

import ContainerWithBackground from 'common/components/ContainerWithBackground';
import ImageGalleryOverlay from 'common/components/ImageGalleryOverlay';
import { Link } from 'common/components/Link';
import { LinkRegularLR1 } from 'common/components/Typography';
import { EventBusEvent } from 'common/enums/EventBus';
import { TrackingEvent, TrackingEventElement } from 'common/enums/Reclamation';
import { useSubscription } from 'common/hooks/subscription';
import { useLocalization } from 'common/providers/LocaleProvider';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { ReclamationRecapItem } from 'common/types/reclamation';
import Card from './Card';
import type {
  ImageGalleryOpenAction,
  ImageGalleryToggleAction,
} from './Card/AdditionalInfo/Pictures';
import { useActionHandlers, useUploadedPicturesStateChange } from './hooks';

export interface ReclamationRecapCardListProps {
  reclamationId: string;
  reclamationType: ReclamationType;
  addMoreProductsUrl: Nullable<string>;
  items: ReclamationRecapItem[];
  totalItemsCount: number;
  isReadOnly: boolean;
  isExternalMarketplace?: boolean;
}

const CardContainer = styled(ContainerWithBackground)<SpaceProps>`
  ${space};
`;
const cardContainerStyles: ComponentProps<typeof CardContainer> = {
  px: ['sp_12', 'sp_16', null, 'sp_24'],
  py: ['sp_16', null, null, 'sp_20'],
  mb: 'sp_4',
};

const AddMoreLink = styled(Link)`
  ${space};
`;
const addMoreLinkStyles = {
  display: 'block',
  mt: 'sp_16',
  ml: ['sp_12', 'sp_16', 'sp_0'],
};

const isImageGalleryOverlayReady = (
  imageGalleryState: Nullable<ImageGalleryToggleAction>
): imageGalleryState is ImageGalleryOpenAction => Boolean(imageGalleryState?.isOpen);

const ReclamationRecapCardList = ({
  reclamationId,
  reclamationType,
  addMoreProductsUrl,
  items: initialItems,
  totalItemsCount,
  isReadOnly,
  isExternalMarketplace,
}: ReclamationRecapCardListProps) => {
  const [items, setItems] = useState(initialItems);
  const [isLoading, setIsLoading] = useState(false);
  const [imageGalleryState] = useSubscription<ImageGalleryToggleAction>(
    EventBusEvent.ReclamationImageGalleryToggle
  );
  const { t } = useLocalization();
  const errorMessage = t('h24_something_went_wrong');
  const showAddMoreProductsLink = totalItemsCount !== items.length;
  const addMoreLinkLabel = t('h24_add_more_products');
  const handleAddMoreClick = () => {
    dispatchTrackingEventGA4({
      event: TrackingEvent.ReclamationRecapCardListClick,
      element: TrackingEventElement.Link,
      link_text: addMoreLinkLabel,
    });
  };

  const actionHandlers = useActionHandlers({
    reclamationId,
    reclamationType,
    items,
    errorMessage,
    setIsLoading,
    setItems,
  });

  useUploadedPicturesStateChange(setItems);

  return (
    <section
      data-corgi-id="reclamation-recap-card-list"
      aria-label={
        reclamationType === ReclamationType.Cancellations
          ? t('h24_cancellation_recap_header')
          : t('h24_returns_recap_header')
      }
    >
      {items
        .filter(({ article }) => !!article)
        .map(({ id, article, ...restProps }) => (
          <CardContainer key={id} {...cardContainerStyles}>
            <Card
              {...restProps}
              id={id}
              reclamationId={reclamationId}
              imagePath={article?.images?.[0]?.path ?? null}
              title={article?.name ?? ''}
              subtitle={article?.variantName ?? ''}
              isDisabled={isLoading}
              isReadOnly={isReadOnly}
              isCancellation={reclamationType === ReclamationType.Cancellations}
              isExternalMarketplace={isExternalMarketplace}
              {...(!isReadOnly ? actionHandlers : {})}
            />
          </CardContainer>
        ))}
      {showAddMoreProductsLink && addMoreProductsUrl && (
        <AddMoreLink href={addMoreProductsUrl} onClick={handleAddMoreClick} {...addMoreLinkStyles}>
          <LinkRegularLR1 textDecoration="underline">{addMoreLinkLabel}</LinkRegularLR1>
        </AddMoreLink>
      )}
      {isImageGalleryOverlayReady(imageGalleryState) && (
        <ImageGalleryOverlay
          withFixedSize
          items={imageGalleryState.items}
          activeItem={imageGalleryState.activeItem}
          thumbnails={imageGalleryState.thumbnails}
          closeOverlayHandler={imageGalleryState.toggleHandler}
          ariaLabel={t('h24_photo_video_gallery')}
        />
      )}
    </section>
  );
};

export default ReclamationRecapCardList;
